/**
 * McpAppContainer — MCP App host component for the Studio FE
 *
 * Implements the host side of the MCP Apps protocol (@modelcontextprotocol/ext-apps v1.7.2).
 * Reference: packages/reachy-host/src/host.ts (the correct implementation).
 *
 * Protocol flow:
 *   1. App sends ui/initialize → Host responds with capabilities
 *   2. App sends ui/notifications/initialized → Host pushes pending tool-result
 *   3. App calls tools/call → Host proxies to backend, returns result
 *   4. App calls ui/update-model-context → Host defers context for next message
 *   5. App sends ui/message → Host injects into chat + triggers agent turn
 */

import React, {useCallback, useEffect, useImperativeHandle, forwardRef, useRef, useState} from 'react'
import {Box, CircularProgress, Typography, IconButton} from '@mui/material'
import {
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material'
import type {McpAppData} from './types'

// ── Constants ─────────────────────────────────────────────────────────────────

const SANDBOX_PERMISSIONS = 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin'

// ── Types ─────────────────────────────────────────────────────────────────────

interface JsonRpcMessage {
  jsonrpc: '2.0'
  id?: string | number
  method?: string
  params?: Record<string, unknown>
  result?: unknown
  error?: {code: number; message: string; data?: unknown}
}

export interface McpAppContainerHandle {
  /** Send a tool-input notification to the iframe app (used by mcp_app_input SSE events) */
  sendToolInput: (args: Record<string, unknown>) => void
  /** Send a tool-result notification to the iframe app */
  sendToolResult: (result: Record<string, unknown>) => void
  /** The serverId this container is connected to */
  serverId: string
}

interface McpAppContainerProps extends McpAppData {
  /** Base URL for the backend API (e.g. http://localhost:8787) */
  apiBaseUrl: string
  /** Session ID for MCP tool calls */
  sessionId?: string
  /** Callback when the MCP App sends a message to inject into the chat */
  onMessage?: (text: string) => void
  /** Callback after the MCP App successfully calls a server tool (host uses it to react to
   *  panel-side edits — e.g. refresh draft state after update_classification/update_plan). */
  onToolCalled?: (toolName: string) => void
  /** Current theme — passed to the app */
  theme?: 'light' | 'dark'
  /** Initial height in pixels (default: 400) */
  initialHeight?: number
  /** When true, renders without its own header/border (embedded in a host panel) */
  panelMode?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────────

const McpAppContainer = forwardRef<McpAppContainerHandle, McpAppContainerProps>(({
  serverId,
  resourceUri,
  toolName,
  toolInput,
  toolResult,
  apiBaseUrl,
  sessionId,
  onMessage,
  onToolCalled,
  theme = 'light',
  initialHeight = 400,
  panelMode = false,
}, ref) => {
  // A full http(s) URL means the MCP App is served at its own origin (CF Pages in
  // prod, the Vite dev server on :4500 locally). Load it as a real cross-origin
  // iframe via `src` so its own relative assets (e.g. dev-mode `/src/app.ts`)
  // resolve against THAT origin. Fetching the HTML and injecting via `srcDoc`
  // would give the iframe the parent origin (:3002) and no base URL, so those
  // relative assets 404 — which is exactly what breaks against the dev server
  // (prod is a single inlined file, so srcDoc happened to work there).
  const isExternalUrl = resourceUri.startsWith('https://') || resourceUri.startsWith('http://')

  // The origin the iframe runs at. External URL → its own origin; proxy/srcDoc path
  // → the app's own origin (a srcDoc iframe inherits the parent origin). We pin BOTH
  // sides of the postMessage bridge to this: outbound messages target it (never '*',
  // which would leak host context / tool results to whatever origin occupies the
  // frame), and inbound messages are rejected unless event.origin matches. The
  // window-identity check alone is insufficient once the iframe is cross-origin —
  // it can't tell a trusted app from an origin the frame was navigated/redirected to.
  const expectedOrigin = isExternalUrl ? new URL(resourceUri).origin : window.location.origin

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  // External URLs load via iframe src (no fetch), so they don't wait on a fetch.
  const [loading, setLoading] = useState(!isExternalUrl)
  const [error, setError] = useState<string | null>(null)
  const [iframeHeight, setIframeHeight] = useState(initialHeight)
  const [expanded, setExpanded] = useState(false)

  // Pending tool result — pushed to the app AFTER it sends ui/notifications/initialized
  const pendingToolResultRef = useRef<Record<string, unknown> | null>(
    toolResult ? {
      content: toolResult.content,
      structuredContent: toolResult.structuredContent,
      isError: toolResult.isError ?? false,
    } : null
  )

  // Deferred model context — held until the next ui/message (two-step pattern)
  const deferredContextRef = useRef<string | null>(null)
  const prevToolResultRef = useRef(toolResult)

  // ── Fetch the HTML resource ──────────────────────────────────────────────

  useEffect(() => {
    if (isExternalUrl) return // loaded via iframe src, no fetch needed
    let cancelled = false

    async function fetchResource() {
      setLoading(true)
      setError(null)

      try {
        // Proxy path: backend serves the MCP App HTML, injected via srcDoc below.
        const url = `${apiBaseUrl}/api/mcp/resource?serverId=${encodeURIComponent(serverId)}&uri=${encodeURIComponent(resourceUri)}`
        const response = await fetch(url, {credentials: 'include'})

        if (!response.ok) {
          const body = await response.json().catch(() => ({}))
          throw new Error(body.message ?? `Failed to load MCP App: ${response.status}`)
        }

        const html = await response.text()
        if (!cancelled) {
          setHtmlContent(html)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load MCP App')
          setLoading(false)
        }
      }
    }

    fetchResource()
    return () => { cancelled = true }
  }, [apiBaseUrl, serverId, resourceUri, isExternalUrl])

  // ── Send message to iframe ──────────────────────────────────────────────

  const sendToIframe = useCallback((message: JsonRpcMessage) => {
    if (iframeRef.current?.contentWindow) {
      // Pin the target origin so host context / tool results only ever reach the
      // intended app origin, not whatever origin the frame might be navigated to.
      iframeRef.current.contentWindow.postMessage(message, expectedOrigin)
    }
  }, [expectedOrigin])

  // When toolResult prop changes (e.g., switching from extraction to survey review),
  // push the new data to the already-loaded iframe immediately
  useEffect(() => {
    if (toolResult !== prevToolResultRef.current && iframeLoadedRef.current) {
      prevToolResultRef.current = toolResult
      sendToIframe({
        jsonrpc: '2.0',
        method: 'ui/notifications/tool-result',
        params: {
          content: toolResult.content,
          structuredContent: toolResult.structuredContent,
          isError: toolResult.isError ?? false,
        },
      })
    }
  }, [toolResult, sendToIframe])

  // ── Imperative handle — parent can push new tool results ────────────────

  useImperativeHandle(ref, () => ({
    sendToolInput: (args: Record<string, unknown>) => {
      sendToIframe({
        jsonrpc: '2.0',
        method: 'ui/notifications/tool-input',
        params: {arguments: args},
      })
    },
    sendToolResult: (result: Record<string, unknown>) => {
      sendToIframe({
        jsonrpc: '2.0',
        method: 'ui/notifications/tool-result',
        params: result,
      })
    },
    serverId,
  }), [sendToIframe, serverId])

  // ── PostMessage handler ────────────────────────────────────────────────

  const handleMessage = useCallback(async (event: MessageEvent) => {
    // Window-identity AND origin must both match. Identity alone can't distinguish
    // the trusted app from an origin the (cross-origin) frame was navigated to;
    // these messages drive chat injection and credentialed backend tool calls.
    if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return
    if (event.origin !== expectedOrigin) return

    const msg = event.data as JsonRpcMessage
    if (msg.jsonrpc !== '2.0') return

    // ── 1. ui/initialize — App requests initialization ──────────────────
    if (msg.method === 'ui/initialize' && msg.id !== undefined) {
      iframeLoadedRef.current = true
      sendToIframe({
        jsonrpc: '2.0',
        id: msg.id,
        result: {
          protocolVersion: '2026-01-26',
          hostInfo: {name: 'Rival OS Studio', version: '1.0.0'},
          hostCapabilities: {sampling: {}, tools: {listChanged: true}},
          hostContext: {
            theme,
            displayMode: expanded ? 'fullscreen' : 'inline',
            containerDimensions: {maxHeight: 800},
            apiBaseUrl,
            sessionId,
          },
        },
      })
      // Do NOT push tool result here — wait for ui/notifications/initialized
      return
    }

    // ── 2. ui/notifications/initialized — App signals ready ─────────────
    if (msg.method === 'ui/notifications/initialized') {
      // Push host context so the app has apiBaseUrl and sessionId
      sendToIframe({
        jsonrpc: '2.0',
        method: 'ui/notifications/host-context-changed',
        params: { theme, displayMode: expanded ? 'fullscreen' : 'inline', apiBaseUrl, sessionId },
      })

      // Now push the pending tool result
      if (pendingToolResultRef.current) {
        sendToIframe({
          jsonrpc: '2.0',
          method: 'ui/notifications/tool-result',
          params: pendingToolResultRef.current,
        })
        pendingToolResultRef.current = null
      } else {
        // Fallback: fetch current data from the server
        try {
          const res = await fetch(`${apiBaseUrl}/api/mcp/tool`, {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({tool: toolName, sessionId}),
          })
          const result = await res.json()
          sendToIframe({
            jsonrpc: '2.0',
            method: 'ui/notifications/tool-result',
            params: result,
          })
        } catch (err) {
          console.error('[McpAppContainer] Failed to fetch tool data:', err)
        }
      }
      return
    }

    // ── 3. tools/call — App calls a server tool ─────────────────────────
    if (msg.method === 'tools/call' && msg.id !== undefined) {
      const params = msg.params as {name: string; arguments: Record<string, unknown>} | undefined
      if (!params?.name) {
        sendToIframe({jsonrpc: '2.0', id: msg.id, error: {code: -32602, message: 'Missing tool name'}})
        return
      }

      try {
        const res = await fetch(`${apiBaseUrl}/api/mcp/tool`, {
          method: 'POST',
          credentials: 'include',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            serverId,
            tool: params.name,
            arguments: params.arguments ?? {},
            sessionId,
          }),
        })
        const result = await res.json()
        sendToIframe({jsonrpc: '2.0', id: msg.id, result})
        // Surface the tool call to the host (e.g. so RosApp can refresh draft state after a
        // panel-side edit like update_classification/update_plan flips the draft dirty).
        onToolCalled?.(params.name)
      } catch (err) {
        sendToIframe({
          jsonrpc: '2.0',
          id: msg.id,
          error: {code: -32603, message: err instanceof Error ? err.message : 'Tool call failed'},
        })
      }
      return
    }

    // ── 4. ui/update-model-context — App pushes context for next turn ────
    if (msg.method === 'ui/update-model-context' && msg.id !== undefined) {
      const contentBlocks = msg.params?.content as Array<{type: string; text: string}> | undefined
      if (Array.isArray(contentBlocks)) {
        deferredContextRef.current = contentBlocks
          .filter(c => c.type === 'text')
          .map(c => c.text)
          .join('\n')
      }
      sendToIframe({jsonrpc: '2.0', id: msg.id, result: {}})
      return
    }

    // ── 5. ui/message — App sends a message to inject into chat ──────────
    if (msg.method === 'ui/message' && msg.id !== undefined) {
      const contentBlocks = msg.params?.content as Array<{type: string; text: string}> | undefined
      let text = ''
      if (Array.isArray(contentBlocks)) {
        text = contentBlocks.filter(c => c.type === 'text').map(c => c.text).join('\n')
      }

      // Acknowledge immediately
      sendToIframe({jsonrpc: '2.0', id: msg.id, result: {}})

      if (text && onMessage) {
        // Two-step pattern: prepend deferred context from ui/update-model-context
        if (deferredContextRef.current) {
          text = `[MCP App Context]\n${deferredContextRef.current}\n\n${text}`
          deferredContextRef.current = null
        }
        // Inject into chat and trigger a new agent turn
        onMessage(text)
      }
      return
    }

    // ── ui/openLink ──────────────────────────────────────────────────────
    if (msg.method === 'ui/openLink') {
      const uri = (msg.params as {uri?: string})?.uri
      if (uri) window.open(uri, '_blank', 'noopener,noreferrer')
      if (msg.id) sendToIframe({jsonrpc: '2.0', id: msg.id, result: {}})
      return
    }

    // ── ui/downloadFile ──────────────────────────────────────────────────
    if (msg.method === 'ui/downloadFile') {
      const params = msg.params as {uri?: string; filename?: string} | undefined
      if (params?.uri) {
        const a = document.createElement('a')
        a.href = params.uri
        a.download = params.filename ?? 'download'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      if (msg.id) sendToIframe({jsonrpc: '2.0', id: msg.id, result: {}})
      return
    }

    // ── ui/requestDisplayMode ────────────────────────────────────────────
    if (msg.method === 'ui/requestDisplayMode') {
      const mode = (msg.params as {mode?: string})?.mode
      setExpanded(mode === 'fullscreen')
      if (msg.id) sendToIframe({jsonrpc: '2.0', id: msg.id, result: {mode: mode === 'fullscreen' ? 'fullscreen' : 'inline'}})
      return
    }

    // ── ui/notifications/sizeChanged ─────────────────────────────────────
    if (msg.method === 'ui/notifications/sizeChanged') {
      const height = (msg.params as {height?: number})?.height
      if (height && height > 0) setIframeHeight(Math.min(height, 800))
      return
    }

    // ── ping ─────────────────────────────────────────────────────────────
    if (msg.method === 'ping' && msg.id !== undefined) {
      sendToIframe({jsonrpc: '2.0', id: msg.id, result: {}})
      return
    }
  }, [sendToIframe, apiBaseUrl, serverId, sessionId, toolName, theme, expanded, onMessage, onToolCalled, expectedOrigin])

  // ── Register message listener ──────────────────────────────────────────

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  // ── Send host context change when theme/display mode changes ───────────
  const iframeLoadedRef = useRef(false)
  // Bumped on retry to force a fresh iframe mount (re-navigates `src`).
  const [reloadKey, setReloadKey] = useState(0)

  // External-URL load watchdog: a cross-origin iframe gives no reliable load-failure
  // signal, so if the app hasn't completed the ui/initialize handshake within the
  // timeout we surface an actionable error (down dev server / failed CF Pages) with
  // a retry, instead of leaving a silent blank iframe.
  useEffect(() => {
    if (!isExternalUrl) return
    iframeLoadedRef.current = false
    const timer = setTimeout(() => {
      if (!iframeLoadedRef.current) {
        setError(`MCP App did not load from ${expectedOrigin}. The app may be unavailable.`)
      }
    }, 10000)
    return () => clearTimeout(timer)
  }, [isExternalUrl, resourceUri, reloadKey, expectedOrigin])

  useEffect(() => {
    if (!iframeLoadedRef.current) return
    sendToIframe({
      jsonrpc: '2.0',
      method: 'ui/notifications/host-context-changed',
      params: {
        theme,
        displayMode: expanded ? 'fullscreen' : 'inline',
      },
    })
  }, [theme, expanded, sendToIframe])

  // ── Retry handler ──────────────────────────────────────────────────────

  const handleRetry = useCallback(() => {
    setError(null)
    pendingToolResultRef.current = toolResult ? {
      content: toolResult.content,
      structuredContent: toolResult.structuredContent,
      isError: toolResult.isError ?? false,
    } : null

    // External path: re-mount the iframe to re-navigate `src` (re-arms the watchdog).
    if (isExternalUrl) {
      setReloadKey(k => k + 1)
      return
    }

    // Proxy path: re-fetch the backend-served HTML for srcDoc injection.
    setHtmlContent(null)
    setLoading(true)
    const url = `${apiBaseUrl}/api/mcp/resource?serverId=${encodeURIComponent(serverId)}&uri=${encodeURIComponent(resourceUri)}`
    fetch(url, {credentials: 'include'})
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}))
          throw new Error(body.message ?? `Failed to load: ${response.status}`)
        }
        return response.text()
      })
      .then((html) => {
        setHtmlContent(html)
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load MCP App')
        setLoading(false)
      })
  }, [apiBaseUrl, serverId, resourceUri, toolResult, isExternalUrl])

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 1.5, py: 4,
        border: '1.5px solid', borderColor: 'primary.main',
        borderRadius: '12px', bgcolor: 'background.paper',
        opacity: 0.8,
      }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading MCP App...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 1, py: 3, px: 2,
        border: '1.5px solid', borderColor: 'error.main',
        borderRadius: '12px', bgcolor: 'background.paper',
      }}>
        <ErrorIcon color="error" />
        <Typography variant="body2" color="error">{error}</Typography>
        <IconButton size="small" onClick={handleRetry} sx={{mt: 0.5}}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
    )
  }

  // Proxy path waits for fetched HTML; external path renders the iframe immediately.
  if (!isExternalUrl && !htmlContent) return null

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      border: panelMode ? 'none' : '1.5px solid',
      borderColor: '#5b7fa6',
      borderRadius: panelMode ? 0 : '12px',
      overflow: 'hidden',
      boxShadow: panelMode ? 'none' : '0 2px 12px rgba(91,127,166,0.08)',
      mt: panelMode ? 0 : 1,
      mb: panelMode ? 0 : 0.5,
      height: panelMode ? '100%' : undefined,
      ...(expanded && {
        position: 'fixed',
        top: 16,
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 1300,
        borderRadius: '16px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
      })
    }}>
      {/* MCP App header bar — hidden when embedded in a host panel */}
      {!panelMode && <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 0.75,
        bgcolor: '#edf3f9',
        borderBottom: '1px solid #d0dcea',
      }}>
        <Typography sx={{
          fontSize: '0.68rem', fontWeight: 600, color: '#3d5f82',
          textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1,
        }}>
          {toolName.replace(/_/g, ' ')}
        </Typography>
        <Box sx={{
          fontSize: '0.55rem', fontWeight: 600,
          bgcolor: '#ddeaf6', color: '#3d5f82',
          px: 0.75, py: 0.125, borderRadius: '3px',
        }}>
          MCP App
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded(e => !e)}
          sx={{p: 0.25, color: '#3d5f82'}}
        >
          {expanded ? <CollapseIcon sx={{fontSize: 14}} /> : <ExpandIcon sx={{fontSize: 14}} />}
        </IconButton>
      </Box>}

      {/* Sandboxed iframe. External URL → load cross-origin via `src` (its own
          origin, relative assets resolve correctly). Proxy path → inject fetched
          HTML via `srcDoc`. */}
      <iframe
        key={reloadKey}
        ref={iframeRef}
        {...(isExternalUrl ? { src: resourceUri } : { srcDoc: htmlContent ?? undefined })}
        onError={isExternalUrl ? () => setError(`MCP App failed to load from ${expectedOrigin}.`) : undefined}
        sandbox={SANDBOX_PERMISSIONS}
        style={{
          width: '100%',
          height: expanded ? 'calc(100% - 36px)' : panelMode ? '100%' : iframeHeight,
          border: 'none',
          display: 'block',
          flex: panelMode ? 1 : undefined,
          backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff',
        }}
        title={`MCP App: ${toolName}`}
      />

      {/* Fullscreen backdrop */}
      {expanded && (
        <Box
          onClick={() => setExpanded(false)}
          sx={{
            position: 'fixed', inset: 0, zIndex: -1,
            bgcolor: 'rgba(0,0,0,0.5)',
          }}
        />
      )}
    </Box>
  )
})

McpAppContainer.displayName = 'McpAppContainer'

export default McpAppContainer
